import { type FormatId, type MarkdownTree, PAGE_SCOPE } from '@app/formats'
import {
  Component,
  type ComponentType,
  type ReactNode,
  useEffect,
  useId,
  useState,
} from 'react'
import { MarkdownView } from './formats/MarkdownView'
import { parseTranscriptMarkdown } from './formats/transcript-markdown'
import './format-view.css'

/**
 * Formatted text, read.
 *
 * The one place a body written in a format is drawn, so the same text reads
 * the same wherever it appears — a doc, a task's description, a turn of a
 * conversation and the record of that conversation. A screen names the format
 * and hands over the text; how it is set is decided here and in no screen.
 *
 * It draws the text and nothing around it. A frame, a fill or a height belongs
 * to whatever the text sits in, and so does the ink: a record read back is set
 * quieter than a page being written, and both are this text.
 *
 * **A body the renderer throws on is shown as it was written**, in this view
 * alone. A body is written by anybody, a model included, and one nested deeper
 * than the renderer's stack reaches throws while it is drawn: the words are all
 * still there, and every other view on the page draws as it would have.
 */

/** What an adapter is handed: the text, and the document it is on the page. */
export type FormatRendering = {
  value: string
  /** A tree parsed away from the main thread, when this view is bounded. */
  tree?: MarkdownTree
  /** Names this document among the others on the page. Every id it draws is
   *  made inside it. */
  scope: string
}

function BoundedMarkdownView({ value, scope }: FormatRendering) {
  const [parsed, setParsed] = useState<{
    value: string
    tree: MarkdownTree | null
  } | null>(null)

  useEffect(() => {
    let current = true
    void parseTranscriptMarkdown(value).then((tree) => {
      if (current) setParsed({ value, tree })
    })
    return () => {
      current = false
    }
  }, [value])

  if (!parsed || parsed.value !== value) {
    return <div className="format-view__written">{value}</div>
  }
  return parsed.tree ? (
    <MarkdownView value={value} scope={scope} tree={parsed.tree} />
  ) : (
    <AsWritten value={value} />
  )
}

/**
 * What draws each format. Keyed by every name in the registry, so a format
 * added there fails this app's typecheck until something here can show it.
 */
const VIEWS: Record<FormatId, ComponentType<FormatRendering>> = {
  board: () => null,
  book: () => null,
  html: ({ value }) => <AsWritten value={value} />,
  markdown: MarkdownView,
  motif: ({ value }) => <AsWritten value={value} />,
  slides: () => null,
}

/** A body shown as the characters it holds, with one line saying so. */
export function AsWritten({ value }: { value: string }) {
  return (
    <>
      <p className="format-view__note">Shown as written.</p>
      <div className="format-view__written">{value}</div>
    </>
  )
}

type DrawnState = { failed: boolean; value: string }

/**
 * Draws its children, and the body as written where drawing them throws.
 *
 * A failure belongs to the body that caused it, so a new body is drawn afresh:
 * a reply still arriving may fail part way and draw once it is whole.
 */
export class DrawnOrWritten extends Component<
  { value: string; children: ReactNode },
  DrawnState
> {
  state: DrawnState = { failed: false, value: this.props.value }

  static getDerivedStateFromProps(
    props: { value: string },
    state: DrawnState,
  ): Partial<DrawnState> | null {
    return props.value === state.value
      ? null
      : { failed: false, value: props.value }
  }

  static getDerivedStateFromError(): Partial<DrawnState> {
    return { failed: true }
  }

  render() {
    return this.state.failed ? (
      <AsWritten value={this.props.value} />
    ) : (
      this.props.children
    )
  }
}

export function FormatView({
  format,
  value,
  anchors = 'own',
  bounded = false,
}: {
  format: FormatId
  value: string
  /**
   * Whose the heading ids are.
   *
   * `own` keeps them inside this view, so any number of views on one page —
   * the turns of a conversation — never answer to the same id. `page` is for
   * the ONE document a page is about: its ids are the ones the format's
   * outline names, so a contents beside it reaches them, and a page may hold
   * one such view.
   */
  anchors?: 'own' | 'page'
  /** Parse markdown away from the main thread and draw only a bounded tree. */
  bounded?: boolean
}) {
  const own = useId()
  const View = VIEWS[format]
  const scope = anchors === 'page' ? PAGE_SCOPE : own
  return (
    <div className="format-view" data-format={format} dir="auto">
      <DrawnOrWritten value={value}>
        {bounded && format === 'markdown' ? (
          <BoundedMarkdownView value={value} scope={scope} />
        ) : (
          <View value={value} scope={scope} />
        )}
      </DrawnOrWritten>
    </div>
  )
}
